import { tlogHelper } from "../tlog/tlog-helper";

const columnsMapping = {
  CURRENT_A: "current",
  VOLT_V: "volt",
  TRAVEL_SPEED: "travelSpeed",
  TRUE_ENERGY: "trueEnergy",
  DISTANCE: "distance",
  HEAT: "heat",
  WIRE_SPEED: "wireSpeed",
  OSCILLATION_WIDTH: "oscillationWidth",
  FREQUENCY: "frequency",
  TARGET: "target",
  HORIZONTAL_BIAS: "horizontalBias",
  TOTAL_WIRE_CONSUMED: "totalWireConsumed",
  HEADANGLE: "headAngle",
  ARC_IMPEDANCE: "arcImpedance",
};

const mapTlogGraphColumns = (colorOptions, reportColumns) => {
  return colorOptions?.map((ite) => {
    let changeKeys = [
      columnsMapping.CURRENT_A,
      columnsMapping.VOLT_V,
      columnsMapping.TRAVEL_SPEED,
      columnsMapping.DISTANCE,
      columnsMapping.TRUE_ENERGY,
      columnsMapping.HEAT,
      columnsMapping.WIRE_SPEED,
      columnsMapping.OSCILLATION_WIDTH,
      columnsMapping.FREQUENCY,
      columnsMapping.TARGET,
      columnsMapping.HORIZONTAL_BIAS,
      columnsMapping.TOTAL_WIRE_CONSUMED,
      columnsMapping.HEADANGLE,
      columnsMapping.ARC_IMPEDANCE,
    ];

    if (changeKeys.includes(ite.key)) {
      // Handle both formats of reportColumns
      const getUnit = (key) => {
        // First try the new format (units property on reportColumns)
        if (reportColumns && typeof reportColumns === "object") {
          const units = reportColumns.units || {};

          // Handle different key formats
          switch (key) {
            case columnsMapping.CURRENT_A:
              return units.ArcCurrent || "A";
            case columnsMapping.VOLT_V:
              return units.ArcVolts || "V";
            case columnsMapping.TRAVEL_SPEED:
              return units["Travel Speed"] || "in/min";
            case columnsMapping.TRUE_ENERGY:
              return units["True Energy"] || "KJ/in";
            case columnsMapping.HEAT:
              return units.Heat || "KJ/in";
            case columnsMapping.WIRE_SPEED:
              return units["Wire Speed"] || "in/min";
            case columnsMapping.OSCILLATION_WIDTH:
              return units["Oscillation Width"] || "in";
            case columnsMapping.FREQUENCY:
              return units.Frequency || "cyc/min";
            case columnsMapping.TARGET:
              return units.Target || "A";
            case columnsMapping.HORIZONTAL_BIAS:
              return units["Horizontal Bias"] || "A";
            case columnsMapping.TOTAL_WIRE_CONSUMED:
              return units["Total Wire Consumed"] || "in";
            case columnsMapping.DISTANCE:
              return units.Distance || "in";
            case columnsMapping.HEADANGLE:
              return units["Head Angle"] || "deg";
            case columnsMapping.ARC_IMPEDANCE:
              return units["Arc Impedance"] || "ohms";
            default:
              return "";
          }
        }
        return "";
      };

      const unit = getUnit(ite.key);

      switch (ite.key) {
        case columnsMapping.CURRENT_A:
          return { ...ite, label: `Current (${unit || "A"})` };
        case columnsMapping.VOLT_V:
          return { ...ite, label: `Volt (${unit || "V"})` };
        case columnsMapping.TRAVEL_SPEED:
          return { ...ite, label: `Travel Speed (${unit || "in/min"})` };
        case columnsMapping.TRUE_ENERGY:
          return { ...ite, label: `True Energy (${unit || "KJ/in"})` };
        case columnsMapping.HEAT:
          return { ...ite, label: `Heat (${unit || "KJ/in"})` };
        case columnsMapping.WIRE_SPEED:
          return { ...ite, label: `Wire Speed (${unit || "in/min"})` };
        case columnsMapping.OSCILLATION_WIDTH:
          return { ...ite, label: `Oscillation Width (${unit || "in"})` };
        case columnsMapping.FREQUENCY:
          return { ...ite, label: `Frequency (${unit || "cyc/min"})` };
        case columnsMapping.TARGET:
          return { ...ite, label: `Target (${unit || "A"})` };
        case columnsMapping.HORIZONTAL_BIAS:
          return { ...ite, label: `Horizontal Bias (${unit || "A"})` };
        case columnsMapping.TOTAL_WIRE_CONSUMED:
          return { ...ite, label: `Total Wire Consumed (${unit || "in"})` };
        case columnsMapping.DISTANCE:
          return { ...ite, label: `Distance (${unit || "in"})` };
        case columnsMapping.HEADANGLE:
          return { ...ite, label: `Head Angle (${unit || "deg"})` };
        case columnsMapping.ARC_IMPEDANCE:
          return { ...ite, label: `Arc Impedance (${unit || "ohms"})` };
      }
    } else {
      return ite;
    }
  });
};

const mapTlogGraphUnits = (value, reportColumns) => {
  if (!reportColumns) return "";

  // Handle both formats
  const units = reportColumns.units || {};

  switch (value) {
    case columnsMapping.TRAVEL_SPEED:
      return units["Travel Speed"];
    case columnsMapping.VOLT_V:
      return units.ArcVolts;
    case columnsMapping.CURRENT_A:
      return units.ArcCurrent;
    case columnsMapping.WIRE_SPEED:
      return units["Wire Speed"];
    case columnsMapping.OSCILLATION_WIDTH:
      return units["Oscillation Width"];
    case columnsMapping.TARGET:
      return units.Target;
    case columnsMapping.HORIZONTAL_BIAS:
      return units["Horizontal Bias"];
    case columnsMapping.FREQUENCY:
      return units.Frequency;
    case columnsMapping.TOTAL_WIRE_CONSUMED:
      return units["Total Wire Consumed"];
    case columnsMapping.TRUE_ENERGY:
      return units["True Energy"];
    case columnsMapping.HEAT:
      return units.Heat;
    default:
      return "";
  }
};

const getMinMaxValuesForGraph = ({
  column,
  tlogData,
  tableLimitData,
  type,
  reportColumns,
}) => {
  let avgTlogItem = tlogData[0];

  if (!avgTlogItem) {
    return {
      min: 0,
      max: 0,
      allLimits: [],
    };
  }

  // Extract pass name and zone based on type
  let passNameExtracted = "";

  if (type === "Lead") {
    passNameExtracted =
      avgTlogItem?.lead_pass_name_actual ||
      avgTlogItem?.lead_pass_name ||
      avgTlogItem?.pass_name;
  } else if (type === "Trail") {
    passNameExtracted =
      avgTlogItem?.trail_pass_name_actual ||
      avgTlogItem?.trail_pass_name ||
      avgTlogItem?.pass_name;
  } else {
    passNameExtracted =
      avgTlogItem?.pass_name ||
      avgTlogItem?.lead_pass_name ||
      avgTlogItem?.trail_pass_name;
  }

  // Handle HEAD case
  if (passNameExtracted?.includes("Head")) {
    passNameExtracted = "HEAD";
  }

  // Get job number
  let job_number = avgTlogItem?.job_number;

  // Filter table limits by job number
  let allTableLimitZones = [];
  if (job_number) {
    allTableLimitZones = tableLimitData?.filter(
      (tableLimit) => tableLimit.job_number == job_number
    );
  }

  // First try to match by pass name
  let filteredLimits = allTableLimitZones?.filter(
    (tableLimit) =>
      tableLimit.pass_name?.toLowerCase() === passNameExtracted?.toLowerCase()
  );

  if (!filteredLimits || filteredLimits.length === 0) {
    return { min: 0, max: 0, allLimits: [] };
  }

  // Map column name to table limit column name
  const columnMapping = {
    travelSpeed: "travel_speed",
    volts: "volts",
    current: "current",
    wireSpeed: "wire_speed",
    oscillationWidth: "oscillation_width",
    trueEnergy: "heat",
    heat: "calculated_heat",
  };

  const normalizedColumn = columnMapping[column] || column;

  // Try zone-level limits first, then fall back to pass-level limits
  const zoneMinKey = `zone_${normalizedColumn}_min`;
  const zoneMaxKey = `zone_${normalizedColumn}_max`;
  const passMinKey = `${normalizedColumn}_min`;
  const passMaxKey = `${normalizedColumn}_max`;

  const allLimits = filteredLimits.map((limit) => {
    let minValue = limit?.[zoneMinKey] || limit?.[passMinKey];
    let maxValue = limit?.[zoneMaxKey] || limit?.[passMaxKey];

    if (minValue === undefined || minValue === null || minValue === "") {
      minValue = 0;
    }
    if (maxValue === undefined || maxValue === null || maxValue === "") {
      maxValue = 0;
    }

    const calculatedMin = tlogHelper.rangeCalculation(
      minValue,
      normalizedColumn,
      reportColumns,
      false
    );
    const calculatedMax = tlogHelper.rangeCalculation(
      maxValue,
      normalizedColumn,
      reportColumns,
      false
    );

    return {
      min: calculatedMin || 0,
      max: calculatedMax || 0,
      zoneName: limit.zone_name || "Default",
      limitId: limit.id,
    };
  });

  const firstLimit = allLimits[0] || { min: 0, max: 0 };

  return {
    min: firstLimit.min,
    max: firstLimit.max,
    allLimits: allLimits,
  };
};

export const tlogGraphHelper = {
  mapTlogGraphColumns,
  columnsMapping,
  mapTlogGraphUnits,
  getMinMaxValuesForGraph,
};
